import { Controller } from "@hotwired/stimulus"

const HOVER_OPEN_DELAY_MS = 250

export default class extends Controller {
  static values = { urlTemplate: String }

  connect() {
    this.openTimeout = null
    this.closeTimeout = null
    this.activeWrapper = null
    this.openRequested = false
    this.keepOpen = this.keepOpen.bind(this)
    this.scheduleClose = this.scheduleClose.bind(this)
    this.closeOnOutsidePointerDown = this.closeOnOutsidePointerDown.bind(this)
    this.closeOnEscape = this.closeOnEscape.bind(this)
    this.reposition = this.reposition.bind(this)
  }

  disconnect() {
    this.close()
  }

  pointerOver(event) {
    const wrapper = this.wrapperFor(event.target)
    if (!wrapper || (event.relatedTarget instanceof Node && wrapper.contains(event.relatedTarget))) return

    this.scheduleOpen(wrapper)
  }

  pointerOut(event) {
    const wrapper = this.wrapperFor(event.target)
    if (!wrapper || (event.relatedTarget instanceof Node && wrapper.contains(event.relatedTarget))) return

    this.scheduleClose(event)
  }

  focusIn(event) {
    const wrapper = this.wrapperFor(event.target)
    if (wrapper) this.open(wrapper)
  }

  focusOut(event) {
    const wrapper = this.wrapperFor(event.target)
    if (wrapper) this.scheduleClose(event)
  }

  click(event) {
    const trigger = event.target.closest?.("[data-assignment-lifecycle-popover-target='trigger']")
    if (!trigger) return

    event.preventDefault()
    const wrapper = this.wrapperFor(trigger)
    if (wrapper) this.open(wrapper)
  }

  prepareForClone(event) {
    const source = event.detail?.source
    if (source instanceof Element && this.activeWrapper && source.contains(this.activeWrapper)) this.close()
  }

  scheduleOpen(wrapper) {
    this.clearCloseTimeout()
    if (this.activeWrapper === wrapper) {
      this.openRequested = true
      return
    }
    if (this.openTimeout) return

    this.openTimeout = window.setTimeout(() => {
      this.openTimeout = null
      this.open(wrapper)
    }, HOVER_OPEN_DELAY_MS)
  }

  async open(wrapper) {
    this.clearOpenTimeout()
    this.clearCloseTimeout()
    if (this.activeWrapper === wrapper) {
      this.openRequested = true
      return
    }
    if (this.activeWrapper && this.activeWrapper !== wrapper) this.close()

    this.activeWrapper = wrapper
    this.triggerElement = wrapper.querySelector("[data-assignment-lifecycle-popover-target='trigger']")
    this.portalElement = wrapper.querySelector("[data-assignment-lifecycle-popover-target='portal']")
    if (!this.triggerElement || !this.portalElement) return

    this.openRequested = true
    await this.materializePanel(wrapper)
    if (!this.panelElement || this.activeWrapper !== wrapper || !this.openRequested) return

    if (this.portalElement.parentElement !== document.body) document.body.appendChild(this.portalElement)
    this.portalElement.addEventListener("mouseenter", this.keepOpen)
    this.portalElement.addEventListener("mouseleave", this.scheduleClose)
    this.portalElement.classList.remove("hidden")
    this.portalElement.dataset.assignmentLifecyclePopoverOpen = "true"
    this.portalElement.style.position = "fixed"
    this.portalElement.style.zIndex = "1100"
    this.portalElement.style.isolation = "isolate"
    this.triggerElement.setAttribute("aria-expanded", "true")
    document.addEventListener("pointerdown", this.closeOnOutsidePointerDown, true)
    document.addEventListener("keydown", this.closeOnEscape)
    document.addEventListener("turbo:before-cache", this.closeOnEscape)
    window.addEventListener("resize", this.reposition)
    window.addEventListener("scroll", this.reposition, true)
    this.reposition()
  }

  // The canvas emits one URL template and each popover its id, rather than a
  // full URL per chip -- 52kb of HTML and 238 url_for calls on a large plan.
  // A wrapper that still carries its own url-value wins, so surfaces outside
  // the canvas keep working unchanged.
  panelUrlFor(wrapper) {
    const own = wrapper.dataset.assignmentLifecyclePopoverUrlValue
    if (own) return own

    const id = wrapper.dataset.assignmentLifecyclePopoverPersonValue
    if (!id || !this.hasUrlTemplateValue) return null

    return this.urlTemplateValue.replace("PERSON_ID", encodeURIComponent(id))
  }

  async materializePanel(wrapper) {
    const portal = this.portalElement
    const trigger = this.triggerElement
    this.panelElement = portal.querySelector("[data-assignment-lifecycle-popover-panel]")
    if (this.panelElement) return

    const url = this.panelUrlFor(wrapper)
    if (!url) return

    trigger.setAttribute("aria-busy", "true")
    try {
      const response = await fetch(url, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`Lifecycle popover request failed with ${response.status}`)

      portal.innerHTML = await response.text()
      if (this.activeWrapper === wrapper) {
        this.panelElement = portal.querySelector("[data-assignment-lifecycle-popover-panel]")
      }
    } catch (error) {
      console.error(error)
    } finally {
      trigger.removeAttribute("aria-busy")
    }
  }

  scheduleClose(event) {
    const relatedTarget = event?.relatedTarget
    if (relatedTarget instanceof Node && (this.activeWrapper?.contains(relatedTarget) || this.portalElement?.contains(relatedTarget))) return

    this.openRequested = false
    this.clearOpenTimeout()
    this.clearCloseTimeout()
    this.closeTimeout = window.setTimeout(() => this.close(), 90)
  }

  keepOpen() {
    this.clearCloseTimeout()
  }

  close() {
    this.openRequested = false
    this.clearOpenTimeout()
    this.clearCloseTimeout()
    document.removeEventListener("pointerdown", this.closeOnOutsidePointerDown, true)
    if (!this.portalElement) return

    this.portalElement.classList.add("hidden")
    delete this.portalElement.dataset.assignmentLifecyclePopoverOpen
    this.portalElement.removeEventListener("mouseenter", this.keepOpen)
    this.portalElement.removeEventListener("mouseleave", this.scheduleClose)
    this.triggerElement?.setAttribute("aria-expanded", "false")
    document.removeEventListener("keydown", this.closeOnEscape)
    document.removeEventListener("turbo:before-cache", this.closeOnEscape)
    window.removeEventListener("resize", this.reposition)
    window.removeEventListener("scroll", this.reposition, true)
    if (this.activeWrapper?.isConnected && this.portalElement.parentElement !== this.activeWrapper) {
      this.activeWrapper.appendChild(this.portalElement)
    } else if (!this.activeWrapper?.isConnected) {
      this.portalElement.remove()
    }
    this.activeWrapper = null
    this.triggerElement = null
    this.portalElement = null
    this.panelElement = null
  }

  closeOnOutsidePointerDown(event) {
    const target = event.target
    if (!(target instanceof Node)) return
    if (this.activeWrapper?.contains(target) || this.portalElement?.contains(target)) return

    this.close()
  }

  closeOnEscape(event) {
    if (event.type === "keydown" && event.key !== "Escape") return

    const trigger = this.triggerElement
    this.close()
    if (event.type === "keydown") trigger?.focus()
  }

  reposition() {
    if (!this.portalElement || this.portalElement.classList.contains("hidden")) return

    const anchorElement = this.triggerElement.querySelector("[data-assignment-lifecycle-indicator]") || this.triggerElement
    const triggerRect = anchorElement.getBoundingClientRect()
    const margin = 8
    const gap = 2
    const availableHeight = Math.max(0, window.innerHeight - (margin * 2))
    this.portalElement.style.maxHeight = `${availableHeight}px`
    this.portalElement.style.left = "0px"
    this.portalElement.style.top = "0px"
    this.portalElement.style.paddingTop = "0px"
    this.portalElement.style.paddingBottom = "0px"

    const portalRect = this.portalElement.getBoundingClientRect()
    const panelRect = (this.panelElement || this.portalElement).getBoundingClientRect()
    const visibleHeight = Math.min(panelRect.height, Math.max(0, availableHeight - gap))
    const portalHeight = visibleHeight + gap
    const belowTop = triggerRect.bottom
    const aboveTop = triggerRect.top - portalHeight
    const useBelow = belowTop + portalHeight <= window.innerHeight - margin || aboveTop < margin
    const top = useBelow ?
      Math.min(belowTop, Math.max(margin, window.innerHeight - portalHeight - margin)) :
      Math.max(margin, aboveTop)
    const rightLeft = triggerRect.right + gap
    const leftLeft = triggerRect.left - portalRect.width - gap
    const maxLeft = Math.max(margin, window.innerWidth - portalRect.width - margin)
    const left = rightLeft + portalRect.width <= window.innerWidth - margin || leftLeft < margin ?
      Math.min(Math.max(rightLeft, margin), maxLeft) :
      Math.min(Math.max(leftLeft, margin), maxLeft)

    this.portalElement.style.left = `${left}px`
    this.portalElement.style.top = `${top}px`
    this.portalElement.style.paddingTop = useBelow ? `${gap}px` : "0px"
    this.portalElement.style.paddingBottom = useBelow ? "0px" : `${gap}px`
  }

  wrapperFor(target) {
    return target instanceof Element ? target.closest("[data-assignment-lifecycle-popover]") : null
  }

  clearOpenTimeout() {
    if (this.openTimeout) window.clearTimeout(this.openTimeout)
    this.openTimeout = null
  }

  clearCloseTimeout() {
    if (this.closeTimeout) window.clearTimeout(this.closeTimeout)
    this.closeTimeout = null
  }
}
